import { useEffect, useRef, useState } from 'react';
import type { Folder, FolderPermission } from '../api';
import { loadConfig } from '../config';

// The wording describes what the session policy actually allows, not a tidier
// version of it. In particular a "write-only" drop box still lists filenames --
// the session policy grants s3:ListBucket across the user's home tree so clients
// can navigate, and withholds the object reads. Saying "cannot see the contents"
// would be right; saying "cannot see the folder" would not.
const PERMISSIONS: Array<{ value: FolderPermission; label: string; hint: string }> = [
  { value: 'read-write', label: 'Read and write', hint: 'List, download, upload, delete' },
  { value: 'read-only', label: 'Read only', hint: 'List and download; uploads refused' },
  {
    value: 'write-only',
    label: 'Write only (drop box)',
    hint: 'Upload and delete; downloads refused, though filenames stay visible',
  },
];

// Mirrors the path rules in infrastructure/lib/portal-api/profile.mjs, which
// remains the authority -- it runs on every write path, including the migration
// script, and this file is downloadable JavaScript that can be edited by whoever
// is running it. What checking here buys is the moment the message arrives.
//
// That matters most for the wildcard rule. `*` and `?` are ordinary characters in
// an S3 key, so typing `acme-health/*` looks like a reasonable way to say "all of
// their folders" and reads as though it worked. Told at the keystroke, it is a
// typo; told as a 400 after saving a form, it is a puzzle.
//
// Kept to the rules somebody can trip over by typing. Policy-length limits and
// duplicate-prefix collisions depend on the whole profile and stay server-side.
const pathProblem = (value: string, label: string): string | undefined => {
  if (value === '') return undefined; // Emptiness is the input's own `required` to report.
  if (/[*?]/.test(value)) {
    return (
      `${label} cannot contain * or ?. They are ordinary characters in an S3 key, but wildcards ` +
      `in the IAM policy this becomes — so the folder would grant far more than it names.`
    );
  }
  if (value.includes('${')) {
    return `${label} cannot contain "\${": IAM would expand that as a policy variable rather than match it literally.`;
  }
  // Control characters are legal bytes in an S3 key but break logical directory
  // entries and SFTP clients. Unlikely to be typed; entirely possible to paste.
  if (/[\\\u0000-\u001f\u007f]/.test(value)) {
    return `${label} contains a backslash or a control character. Folders are separated by "/".`;
  }
  if (value.startsWith('/') || value.endsWith('/')) {
    return `${label} must not start or end with "/".`;
  }
  if (value.includes('//')) return `${label} must not contain "//".`;
  if (value.split('/').some((segment) => segment === '.' || segment === '..')) {
    return `${label} must not contain "." or ".." segments.`;
  }
  return undefined;
};

export function FolderEditor({
  folders,
  onChange,
}: {
  folders: Folder[];
  onChange: (folders: Folder[]) => void;
}) {
  // Already fetched by App before any page renders, so this resolves from the
  // cache. Undefined while it does, which hides the warning rather than flashing it.
  const [emailConfigured, setEmailConfigured] = useState<boolean>();
  useEffect(() => {
    void loadConfig().then((config) => setEmailConfigured(config.uploadNotifications === true));
  }, []);

  return (
    <div className="folders">
      {folders.map((folder, index) => (
        <FolderRow
          emailConfigured={emailConfigured}
          // Index as the key because a folder has no identity until it is saved,
          // and its path is what is being edited.
          key={index}
          folder={folder}
          onChange={(patch) =>
            onChange(folders.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)))
          }
          onRemove={
            folders.length === 1 ? undefined : () => onChange(folders.filter((_, i) => i !== index))
          }
        />
      ))}
      <button
        type="button"
        className="link"
        onClick={() => onChange([...folders, { path: '', permission: 'read-write' }])}
      >
        Add a folder
      </button>
      <p className="hint">
        Folder names are relative to the user's home directory, which is all they can see over SFTP.
        Nested paths like <code>reports/monthly</code> are allowed; the longest matching folder's
        permission wins. A folder can instead point into another company's tree — tick the box on
        it — which is how an aggregator account reads several partners' files.
      </p>
    </div>
  );
}

function FolderRow({
  folder,
  emailConfigured,
  onChange,
  onRemove,
}: {
  folder: Folder;
  emailConfigured?: boolean;
  onChange: (patch: Partial<Folder>) => void;
  onRemove?: () => void;
}) {
  const pathIssue = pathProblem(folder.path, 'A folder name');
  const writeOnly = folder.permission === 'write-only';
  const sharedIssue =
    folder.sharedPath === undefined ? undefined : pathProblem(folder.sharedPath, 'A shared path');

  return (
    <div className="folder-row">
      <ValidatedInput
        label="Folder path"
        value={folder.path}
        problem={pathIssue}
        onChange={(path) => onChange({ path })}
        placeholder="inbound"
      />
      <select
        aria-label="Permission"
        value={folder.permission ?? 'read-write'}
        onChange={(event) => {
          const permission = event.target.value as FolderPermission;
          // A drop box cannot notify (see below), so switching to one clears the
          // flag rather than leaving a hidden value that makes the save fail.
          onChange(permission === 'write-only' ? { permission, notifyOnUpload: undefined } : { permission });
        }}
      >
        {PERMISSIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <input
        aria-label="Comment"
        value={folder.comment ?? ''}
        onChange={(event) => onChange({ comment: event.target.value || undefined })}
        placeholder="what this folder is for"
      />
      <button
        type="button"
        className="danger link"
        onClick={onRemove}
        disabled={onRemove === undefined}
        title={onRemove === undefined ? 'A user needs at least one folder' : 'Remove'}
      >
        Remove
      </button>
      {/* Full-width, after the controls, so an error appearing does not reflow the
          row it belongs to. */}
      {pathIssue ? (
        <p className="error span">{pathIssue}</p>
      ) : (
        <p className="hint span">
          {PERMISSIONS.find((option) => option.value === (folder.permission ?? 'read-write'))?.hint}
        </p>
      )}
      <div className="span">
        {/* Disabled for a drop box rather than hidden, so it is clear the option
            exists and why it does not apply: the API refuses it, because being
            told about a file you cannot open is a ticket, not a notification. */}
        <label className="inline" title={writeOnly ? 'This user cannot download from a write-only folder' : undefined}>
          <input
            type="checkbox"
            checked={folder.notifyOnUpload === true && !writeOnly}
            disabled={writeOnly}
            onChange={(event) => onChange({ notifyOnUpload: event.target.checked || undefined })}
          />
          Email this user when a file is uploaded here
        </label>
        {folder.notifyOnUpload && !writeOnly && emailConfigured === false && (
          <p className="hint warn">
            Saved, but no email will be sent yet: upload notifications are not set up for this
            environment. See "Upload notifications" in the README.
          </p>
        )}
        <label className="inline">
          <input
            type="checkbox"
            checked={folder.sharedPath !== undefined}
            onChange={(event) => onChange({ sharedPath: event.target.checked ? '' : undefined })}
          />
          Points into another company's folder
        </label>
        {folder.sharedPath !== undefined && (
          <>
            <ValidatedInput
              label="Shared prefix"
              value={folder.sharedPath}
              problem={sharedIssue}
              onChange={(sharedPath) => onChange({ sharedPath })}
              placeholder="acme-health/outbound"
            />
            {sharedIssue && <p className="error">{sharedIssue}</p>}
            <p className="hint warn">
              This grants access to another company's data. Unlike the folder name above, this path
              starts at the top of the bucket, so it includes the owner's username:{' '}
              <code>acme-health/outbound</code>, not <code>outbound</code>. It names one prefix
              exactly — there is no wildcard form, and <code>*</code> is refused. The user sees it as{' '}
              <code>{folder.path || 'the name above'}</code> and cannot tell where it points.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// An input that refuses to let its form be submitted while its value is wrong.
//
// setCustomValidity, rather than a `pattern` attribute: the browser then blocks
// submission *and* shows this exact sentence, instead of us maintaining a regex
// for the blocking and a separate message for the explaining, which would drift.
function ValidatedInput({
  label,
  value,
  problem,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  problem?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.setCustomValidity(problem ?? '');
  }, [problem]);

  return (
    <input
      ref={ref}
      aria-label={label}
      aria-invalid={problem === undefined ? undefined : true}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      spellCheck={false}
      // Both paths this renders are required by the API when present -- a folder
      // with no name, and a share ticked on but left blank, are each rejected
      // there. Saying so here keeps `required` and the rules above from
      // overlapping: emptiness is this attribute's business, content is
      // pathProblem's.
      required
    />
  );
}
